import { createElement, useLayoutEffect, useRef } from 'react'
import { gsap, registerGsap, ScrollTrigger } from '../../anim/gsapSetup'
import { splitToLines, splitToWords, splitToChars, revealTimeline, whenFontsReady } from '../../anim/textReveal'
import { useReducedMotion } from '../../anim/useReducedMotion'
import { cn } from '../../lib/cn'

interface SplitHeadingProps {
  text: string
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'span'
  className?: string
  type?: 'lines' | 'words' | 'chars'
  stagger?: number
  duration?: number
  start?: string
}

/** Heading that reveals line/word/char-by-line on scroll. Falls back to plain text under reduced motion. */
export function SplitHeading({
  text,
  as = 'h2',
  className,
  type = 'lines',
  stagger = 0.09,
  duration = 0.9,
  start = 'top 82%',
}: SplitHeadingProps) {
  const ref = useRef<HTMLHeadingElement>(null)
  const reduced = useReducedMotion()

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || reduced) return
    registerGsap()
    let split: ReturnType<typeof splitToLines> | null = null
    let st: ScrollTrigger | null = null
    const ctx = gsap.context(() => {
      const cancel = whenFontsReady(() => {
        split = type === 'lines' ? splitToLines(el) : type === 'words' ? splitToWords(el) : splitToChars(el)
        const tl = revealTimeline(split.targets, { stagger, duration })
        st = ScrollTrigger.create({ trigger: el, start, animation: tl, once: true })
      })
      return () => {
        cancel()
        st?.kill()
        split?.revert()
      }
    }, el)
    return () => ctx.revert()
  }, [reduced, text, type, stagger, duration, start])

  return createElement(as, { ref, className: cn(className) }, text)
}
